import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star, ArrowRight, Sparkles, Zap, ShoppingCart } from 'lucide-react';
import ServiceContactForm from '../components/ServiceContactForm';
import PortfolioSection from '../components/PortfolioSection';
import { portfolioProjects } from '../mockData';
import { useCart } from '../context/CartContext';

const ServicePageTemplate = ({ 
  title, 
  subtitle, 
  description, 
  icon: Icon,
  features, 
  benefits, 
  pricing,
  color = '#d987ff'
}) => {
  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const { addToCart } = useCart();

  return (
    <div className="bg-white pt-16">
      {/* Hero Section */}
      <section className="relative py-32 px-8 lg:px-16 overflow-hidden bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Icon Badge */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-3 mb-6 px-8 py-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20"
            >
              {Icon && (
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${color}40` }}
                >
                  <Icon size={24} style={{ color: color }} />
                </div>
              )}
              <span className="text-sm font-semibold uppercase tracking-wider text-white">{title}</span>
            </motion.div>
            
            {/* Heading */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white leading-tight tracking-tight" style={{ fontWeight: 700 }}>
              {subtitle || title}
              {subtitle && (
                <>
                  <br />
                  <span className="bg-gradient-to-r from-light-pink to-light-blue bg-clip-text text-transparent">
                    Services
                  </span>
                </>
              )}
            </h1>
            
            {/* Description */}
            <p className="text-xl md:text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed mb-10">
              {description}
            </p>

            {/* CTA Button */}
            <motion.button
              onClick={scrollToForm}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-5 rounded-xl bg-gradient-to-r from-light-pink to-mid-purple text-white font-bold text-sm uppercase tracking-wider shadow-2xl hover:shadow-light-pink/50 transition-all inline-flex items-center gap-3 group"
            >
              Get Started
              <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      {features && features.length > 0 && (
        <section className="py-24 px-8 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" style={{ fontWeight: 700 }}>
                What We <span className="bg-gradient-to-r from-mid-purple to-light-pink bg-clip-text text-transparent">Deliver</span>
              </h2>
              <p className="text-xl text-dark-grey max-w-3xl mx-auto">
                Comprehensive solutions tailored to your needs
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group bg-white rounded-2xl p-8 border border-grey hover:border-transparent hover:shadow-2xl transition-all"
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${color}20` }}
                  >
                    <Check size={28} style={{ color: color }} />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title || feature}</h3>
                  {feature.description && (
                    <p className="text-dark-grey leading-relaxed">{feature.description}</p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      {benefits && benefits.length > 0 && (
        <section className="py-24 px-8 lg:px-16 bg-gradient-to-br from-grey via-white to-grey">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight" style={{ fontWeight: 700 }}>
                  Why Choose Our
                  <br />
                  <span className="bg-gradient-to-r from-mid-purple to-light-pink bg-clip-text text-transparent">
                    {title}
                  </span>
                </h2>
                <p className="text-lg text-dark-grey leading-relaxed mb-8">
                  We combine expertise with innovation to deliver exceptional results that exceed expectations.
                </p>

                <div className="space-y-6">
                  {benefits.map((benefit, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: idx * 0.1 }}
                      className="flex items-start gap-4"
                    >
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${color}30` }}
                      >
                        <Zap size={24} style={{ color: color }} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">{benefit.title || benefit}</h3>
                        {benefit.description && (
                          <p className="text-dark-grey">{benefit.description}</p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="relative h-[500px] rounded-3xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=1000&fit=crop"
                    alt={title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-8 left-8 text-white">
                    <p className="text-2xl font-bold">Expert Solutions</p>
                    <p className="text-white/80">Delivered with Excellence</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      {pricing && pricing.length > 0 && (
        <section className="py-24 px-8 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" style={{ fontWeight: 700 }}>
                Investment <span className="bg-gradient-to-r from-mid-purple to-light-pink bg-clip-text text-transparent">Plans</span>
              </h2>
              <p className="text-xl text-dark-grey max-w-3xl mx-auto">
                Flexible pricing options designed for your success
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {pricing.map((plan, index) => (
                <motion.div
                  key={plan.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`relative bg-white rounded-2xl p-8 border ${
                    plan.popular ? 'border-light-pink shadow-2xl' : 'border-grey'
                  } hover:shadow-2xl transition-all`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <div className="bg-gradient-to-r from-light-pink to-mid-purple text-white px-6 py-2 rounded-full font-bold text-xs uppercase tracking-wider flex items-center gap-2">
                        <Star size={14} fill="currentColor" />
                        Most Popular
                      </div>
                    </div>
                  )}

                  <h3 className="text-2xl font-bold mb-4">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-5xl font-bold" style={{ fontWeight: 700 }}>{plan.price}</span>
                    {plan.period && <span className="text-dark-grey">/{plan.period}</span>}
                  </div>
                  <p className="text-dark-grey mb-6">{plan.description}</p>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <Check size={18} className="text-light-pink flex-shrink-0" />
                        <span className="text-dark-grey">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <motion.button
                    onClick={() => addToCart({
                      id: `${title}-${plan.name}`,
                      type: 'Service Package',
                      name: `${title} - ${plan.name}`,
                      price: plan.price,
                      description: plan.description,
                      features: plan.features
                    })}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-light-pink to-mid-purple text-white shadow-lg'
                        : 'bg-black text-white hover:bg-light-pink'
                    }`}
                  >
                    <ShoppingCart size={18} />
                    Add to Cart
                  </motion.button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Portfolio Section */}
      <PortfolioSection projects={portfolioProjects[title] || []} />

      {/* Contact Form */}
      <ServiceContactForm serviceName={title} />
    </div>
  );
};

export default ServicePageTemplate;
